import React from "react";
import { motion } from "framer-motion";
import { LayoutGrid, Clock, TrendingUp, List } from "lucide-react";

const VendorFeatures: React.FC = () => {
  const features = [
    {
      icon: <LayoutGrid className="w-6 h-6" />,
      title: "Smart Dashboard",
      description:
        "Manage orders, products, customers, and business performance from one intuitive dashboard.",
      direction: { x: -100, y: 0 },
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Flexible Order Management",
      description:
        "Set operating hours, manage pickup or delivery availability, and stay in control of your daily operations.",
      direction: { x: 100, y: 0 },
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Real-Time Order Updates",
      description:
        "Receive instant notifications, track incoming orders, and keep your inventory synchronized automatically.",
      direction: { x: -100, y: 0 },
    },
    {
      icon: <List className="w-6 h-6" />,
      title: "Easy Product Management",
      description:
        "Add food items, groceries, everyday essentials, and Mystery Bags with a simple and organized listing process.",
      direction: { x: 100, y: 0 },
    },
  ];

  return (
    <section id="vendor">
      <div className="min-h-0 bg-linear-to-b from-green-50 to-white flex items-center justify-center px-10 overflow-hidden">
        <div className="max-w-5xl w-full py-24">
          <motion.div
            className="text-center mb-20"
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4 tracking-tight">
              Powerful Features of Smartly Orders
            </h2>
            <div className="w-16 h-1.5 bg-emerald-500 rounded-full mx-auto mt-4 mb-6"></div>
            <p className="text-slate-500 text-lg">
              Everything you need to manage your business, orders, and customers
              in one place.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{
                  x: feature.direction.x,
                  y: feature.direction.y,
                  opacity: 0,
                }}
                whileInView={{
                  x: 0,
                  y: 0,
                  opacity: 1,
                }}
                transition={{
                  duration: 0.8,
                  delay: index * 0.15,
                  ease: [0.25, 0.46, 0.45, 0.94],
                }}
                viewport={{ once: true, amount: 0.3 }}
                whileHover={{
                  scale: 1.02,
                  transition: { duration: 0.2, ease: "easeOut" },
                }}
                className="group flex items-start gap-6 p-7 bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-md hover:border-emerald-200 transition-all duration-300 cursor-pointer"
              >
                <motion.div
                  whileHover={{ rotate: 10, scale: 1.2 }}
                  transition={{ type: "spring", stiffness: 250 }}
                  className="shrink-0 w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600 transition-colors duration-300 group-hover:bg-emerald-100"
                >
                  {feature.icon}
                </motion.div>

                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2 transition-colors duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-slate-500 leading-relaxed text-sm">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default VendorFeatures;
